/**
 * User-related helper functions for the Tracker component
 */

/**
 * Filter users based on search term
 * @param {Array} userProfiles - Array of user profile objects
 * @param {string} userSearchTerm - Search term to filter by
 * @returns {Array} Filtered array of users
 */
export function filterUsers(userProfiles, userSearchTerm) {
  try {
    return userProfiles.filter((user) => {
      const fullName = user.first_name
        ? `${user.first_name} ${user.middle_name || ''} ${user.last_name || ''}`.trim()
        : `${user.company_first_name || user.company_name} ${user.company_last_name || ''}`.trim();

      return fullName.toLowerCase().includes(userSearchTerm.toLowerCase());
    });
  } catch (error) {
    console.error("Error while filtering users:", error);
    return []; // Return empty array on failure to prevent UI crash
  }
}

/**
 * Creates user-related helper functions
 * @param {Object} dependencies - Object containing all necessary dependencies
 */
export const createUserHelpers = ({
  // State values
  isUserStatusDropdownOpen,
  isUserDropdownOpen,
  // Setters
  setIsUserStatusDropdownOpen,
  setUserSearchTerm,
  setUserStatusFilter,
  setSelectedUser,
  setIsUserDropdownOpen,
}) => {
  /**
   * Toggle user status dropdown
   */
  const toggleUserStatusDropdown = () => {
    try {
      setIsUserStatusDropdownOpen(prev => !prev);
      setUserSearchTerm("");
    } catch (error) {
      console.error("Error while toggling user status dropdown:", error);
    }
  };

  /**
   * Handle user status change
   * @param {string} status - Status value ("All", "Active", "Inactive")
   */
  const handleUserStatusChange = (status) => {
    try {
      setUserStatusFilter(status);
    } catch (error) {
      console.error("Error while changing user status:", error);
    }
  };

  /**
   * Handle user search input change
   * @param {Event} e - Input change event
   */
  const handleUserSearchChange = (e) => {
    try {
      setUserSearchTerm(e.target.value);
    } catch (error) {
      console.error("Error updating user search term:", error);
    }
  };

  /**
   * Toggle user dropdown
   */
  const toggleUserDropdown = () => {
    setIsUserDropdownOpen(!isUserDropdownOpen);
  };

  /**
   * Handle user selection
   * @param {string} userId - Selected user ID
   */
  const handleUserSelect = (userId) => {
    setSelectedUser(userId);
    setIsUserDropdownOpen(false);
  };

  return {
    toggleUserStatusDropdown,
    handleUserStatusChange,
    handleUserSearchChange,
    toggleUserDropdown,
    handleUserSelect,
  };
};
